import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiError, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Power, Pencil, Users as UsersIcon, CheckCircle2, Circle } from "lucide-react";
import { PhoneOtpVerifier } from "@/components/PhoneOtpVerifier";

const EMPTY = { email: "", password: "", name: "", phone: "+91", role: "retail_counter" };

// Roles a manager is allowed to assign / edit. Anything above (admin, manager)
// is hidden from managers in the User Management screen and rejected server-side.
const MANAGER_ROLES = ["accountant", "inventory_manager", "retail_counter"];

function UserModal({ initial, currentUser, onClose, onSaved }) {
  const editing = !!initial?.id;
  const isManager = currentUser.role === "manager";
  // Managers cannot reset passwords (admin-only) and can only assign roles
  // within their manageable set.
  const canSetPassword = !isManager;
  const assignableRoles = isManager
    ? Object.fromEntries(Object.entries(ROLES).filter(([k]) => MANAGER_ROLES.includes(k)))
    : ROLES;
  const [form, setForm] = useState(
    initial
      ? { ...initial, phone: initial.phone || "+91", password: "" }
      : { ...EMPTY, role: isManager ? "retail_counter" : EMPTY.role },
  );
  // Firebase ID token from a completed OTP verification. Required whenever
  // the phone is being set/changed. Kept out of `form` because it's a
  // one-shot handshake artefact, not a saved field.
  const [firebaseIdToken, setFirebaseIdToken] = useState(null);
  const [verifiedPhone, setVerifiedPhone] = useState(null);
  const [busy, setBusy] = useState(false);

  const phoneChangedFromInitial = editing && form.phone !== (initial?.phone || "");
  // Phone must be freshly OTP-verified when creating a user, or when the
  // phone number is being changed on an existing user.
  const phoneVerificationRequired = !editing || phoneChangedFromInitial;
  const phoneVerified = firebaseIdToken && verifiedPhone === form.phone;
  const canSave = !phoneVerificationRequired || phoneVerified;

  const save = async () => {
    if (phoneVerificationRequired && !phoneVerified) {
      toast.error("Please verify the phone number with OTP first");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role };
        if (canSetPassword && form.password) body.password = form.password;
        if (phoneChangedFromInitial) {
          body.phone = form.phone;
          body.firebase_id_token = firebaseIdToken;
        }
        await api.patch(`/users/${initial.id}`, body);
      } else {
        const payload = {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          phone: form.phone,
          firebase_id_token: firebaseIdToken,
        };
        await api.post("/users", payload);
      }
      toast.success(`User ${editing ? "updated" : "created"}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="label-eyebrow">{editing ? "Edit" : "New"}</div>
          <h3 className="text-xl font-semibold text-zinc-950">{editing ? "Edit user" : "Create user account"}</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Full name *</label>
            <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Email *</label>
            <input data-testid="user-email-input" type="email" disabled={editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:bg-zinc-100 disabled:text-zinc-500" />
          </div>

          <PhoneOtpVerifier
            phone={form.phone}
            onPhoneChange={(p) => setForm({ ...form, phone: p })}
            onVerified={(idToken, phone) => {
              setFirebaseIdToken(idToken);
              setVerifiedPhone(phone);
            }}
            disabled={busy}
            testidPrefix="user-phone"
          />
          {editing && !phoneChangedFromInitial && initial?.phone_verified && (
            <div className="text-[10px] font-mono text-emerald-700 uppercase tracking-wider flex items-center gap-1" data-testid="user-phone-already-verified">
              <CheckCircle2 className="w-3 h-3" /> On record & verified — edit to change
            </div>
          )}

          {canSetPassword ? (
            <div>
              <label className="label-eyebrow block mb-2">{editing ? "New password (leave blank to keep)" : "Password *"}</label>
              <input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </div>
          ) : (
            editing && (
              <div className="text-[11px] font-mono text-zinc-500 border border-dashed border-zinc-300 px-3 py-2" data-testid="password-locked-note">
                Password resets are admin-only. The user can change their own password.
              </div>
            )
          )}
          <div>
            <label className="label-eyebrow block mb-2">Role *</label>
            <select data-testid="user-role-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {Object.entries(assignableRoles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="user-save-button" disabled={busy || !canSave} onClick={save} className="px-4 py-2 bg-zinc-950 text-white text-sm uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50">
            {busy ? "Saving..." : editing ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const isManager = user.role === "manager";
  const isAdmin = user.role === "admin";

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  // A row is editable by the current user if:
  //   - they're an admin (edit anyone), OR
  //   - they're a manager AND the target is not admin/manager
  // Server enforces this too — the frontend just hides the affordances.
  const canEditRow = (u) => isAdmin || (isManager && MANAGER_ROLES.includes(u.role));
  // Delete is admin-only.
  const canDeleteRow = (u) => isAdmin && u.id !== user.id;

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(`${u.email} ${!u.active ? "enabled" : "disabled"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="users-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Access</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">User Management</h1>
          <p className="text-sm text-zinc-500 mt-2">
            {isManager
              ? "Create and manage accountant, inventory and retail-counter accounts. Only admins can manage other admins or managers, and only admins can reset passwords."
              : "Create role-based access for staff: manager, accountant, inventory and retail counter. Every account requires a phone number verified via SMS OTP."}
          </p>
        </div>
        <button data-testid="add-user-button" onClick={() => { setEdit(null); setOpen(true); }} className="bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold px-4 py-2.5 hover:bg-zinc-800 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New user
        </button>
      </div>

      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-semibold">Name</th>
              <th className="text-left px-5 py-3 font-semibold">Email</th>
              <th className="text-left px-5 py-3 font-semibold">Phone</th>
              <th className="text-left px-5 py-3 font-semibold">Role</th>
              <th className="text-left px-5 py-3 font-semibold">Created</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-zinc-500"><UsersIcon className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No users.</td></tr>
            )}
            {users.map((u) => {
              const editable = canEditRow(u);
              const deletable = canDeleteRow(u);
              return (
                <tr key={u.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-5 py-3 text-zinc-950 font-medium">{u.name}</td>
                  <td className="px-5 py-3 font-mono text-zinc-700">{u.email}</td>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-700">
                    {u.phone ? (
                      <span className="inline-flex items-center gap-1">
                        {u.phone}
                        {u.phone_verified
                          ? <CheckCircle2 className="w-3 h-3 text-emerald-600" data-testid={`user-phone-verified-${u.email}`} />
                          : <Circle className="w-3 h-3 text-zinc-300" />}
                      </span>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-[11px] uppercase tracking-wider px-2 py-1 border border-zinc-300 bg-white">{ROLES[u.role]}</span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-500">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3">
                    {u.active === false
                      ? <span className="text-[11px] uppercase tracking-wider px-2 py-1 border border-zinc-300 bg-zinc-100 text-zinc-700">Inactive</span>
                      : <span className="text-[11px] uppercase tracking-wider px-2 py-1 border border-emerald-200 bg-emerald-50 text-emerald-700">Active</span>}
                  </td>
                  <td className="px-5 py-3 text-right space-x-1">
                    {editable ? (
                      <>
                        <button data-testid={`user-toggle-${u.email}`} onClick={() => toggleActive(u)} title="Toggle active" className="p-1.5 border border-zinc-300 hover:bg-zinc-100 inline-flex"><Power className="w-3.5 h-3.5" /></button>
                        <button data-testid={`user-edit-${u.email}`} onClick={() => { setEdit(u); setOpen(true); }} className="p-1.5 border border-zinc-300 hover:bg-zinc-100 inline-flex"><Pencil className="w-3.5 h-3.5" /></button>
                      </>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-400" data-testid={`user-locked-${u.email}`}>Locked</span>
                    )}
                    {deletable && (
                      <button data-testid={`user-delete-${u.email}`} onClick={() => remove(u)} className="p-1.5 border border-zinc-300 hover:bg-rose-50 hover:border-rose-300 inline-flex"><Trash2 className="w-3.5 h-3.5 text-rose-600" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && <UserModal initial={edit} currentUser={user} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
